import React, { useEffect, useState } from "react";
import { FaShareAlt, FaFacebook, FaWhatsapp, FaTwitter, FaLink, FaEye, FaCalendarAlt, FaUser } from "react-icons/fa";
import fallbackImage1 from "../assets/hero.webp";
import fallbackImage2 from "../assets/hero.jpg";
import HeartbeatLoader from './HeartbeatLoader';

const FeaturedArticles = () => {
  const [blogs, setBlogs] = useState([]);
  const [imageUrls, setImageUrls] = useState({});
  const [showShare, setShowShare] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const checkImageExists = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  };

  const findValidImageUrl = async (postId) => {
    const url = `https://www.pcds.co.in/cpm/imagesarticle/b${postId}.png`;
    const exists = await checkImageExists(url);
    return exists ? url : (Math.random() > 0.5 ? fallbackImage1 : fallbackImage2);
  };

  const getBlogUrl = (blog) => `/article/${blog.url}.php`;

  const toggleShare = (id) => {
    setShowShare((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const shareBlog = (platform, blog) => {
    const blogUrl = `${window.location.origin}${getBlogUrl(blog)}`;
    const shareText = `Check out this medical article: ${blog.title}`;
    const ogImageUrl = imageUrls[blog.id] || fallbackImage1;

    switch (platform) {
      case "facebook":
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(blogUrl)}&picture=${encodeURIComponent(ogImageUrl)}`);
        break;
      case "whatsapp":
        window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText} ${blogUrl}`)}`);
        break;
      case "twitter":
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(blogUrl)}`);
        break;
      case "copy":
        navigator.clipboard.writeText(blogUrl).then(() => {
          alert("Link copied to clipboard!");
        });
        break;
      default:
        break;
    }

    setShowShare({});
  };

  const formatViewCount = (count) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count;
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-IN', options);
  };

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setIsLoading(true);
        const res = await fetch("https://www.pcds.co.in/blogapi.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ 
            uri: "get",
            website: "http://www.thehealing.in",
            page: 1
          }),
        });
        const data = await res.json();

        const processed = data.map((blog) => ({
          ...blog,
          title: (blog.title || "Untitled Article").replace(/&ndash;/g, '–'),
          excerpt: blog.body?.replace(/<[^>]*>?/gm, '').substring(0, 100) + '...' || 'Read more...',
          date: blog.datec
            ? new Date(blog.datec).toLocaleDateString("en-IN", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "Unknown date",
          viewCount: blog.viewcount || 0,
          keywords: blog.keywords || "",
          created_at: blog.created_at || new Date().toISOString(),
          url: blog.url || blog.id.toString(),
        }));

        // Take only first 3 articles
        setBlogs(processed.slice(0, 3));

        // Preload images for these 3 articles
        const imgObj = {};
        for (const blog of processed.slice(0, 3)) {
          imgObj[blog.id] = await findValidImageUrl(blog.id);
        }
        setImageUrls(imgObj);
      } catch (e) {
        console.error("Error:", e);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  if (isLoading) {
    return (
      <div className="bg-[#f8faf8] min-h-[300px] flex items-center justify-center">
        <HeartbeatLoader />
      </div>
    );
  }

  return (
    <div className="py-0 bg-gray-50">
      <div className="container mx-auto px-4">
       
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {blogs.map((blog) => (
            <div 
              key={blog.id} 
              className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              <div className="relative h-48 overflow-hidden group">
                <img
                  src={imageUrls[blog.id] || fallbackImage1}
                  alt={blog.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <button
                  onClick={() => toggleShare(blog.id)}
                  className="absolute top-3 right-3 bg-white p-2 rounded-full shadow-md hover:bg-gray-100 transition-all duration-300 hover:scale-110"
                >
                  <FaShareAlt className="text-gray-700" />
                </button>
                {showShare[blog.id] && (
                  <div className="absolute top-14 right-3 bg-white rounded-lg shadow-lg p-2 flex flex-col space-y-2 z-10">
                    <button
                      onClick={() => shareBlog("facebook", blog)}
                      className="flex items-center text-blue-600 text-sm hover:underline px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                    >
                      <FaFacebook className="mr-2" /> Facebook
                    </button>
                    <button
                      onClick={() => shareBlog("whatsapp", blog)}
                      className="flex items-center text-green-600 text-sm hover:underline px-2 py-1 rounded hover:bg-green-50 transition-colors"
                    >
                      <FaWhatsapp className="mr-2" /> WhatsApp
                    </button>
                    <button
                      onClick={() => shareBlog("twitter", blog)}
                      className="flex items-center text-blue-400 text-sm hover:underline px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                    >
                      <FaTwitter className="mr-2" /> Twitter
                    </button>
                    <button
                      onClick={() => shareBlog("copy", blog)}
                      className="flex items-center text-gray-700 text-sm hover:underline px-2 py-1 rounded hover:bg-gray-50 transition-colors"
                    >
                      <FaLink className="mr-2" /> Copy Link
                    </button>
                  </div>
                )}
              </div>
              <div className="p-6">
                <div className="flex items-center text-xs text-gray-500 mb-2">
                  <span className="flex items-center mr-3">
                    <FaUser className="mr-1" /> {blog.username || "Medical Expert"}
                  </span>
                  <span className="flex items-center">
                    <FaCalendarAlt className="mr-1" /> {formatDate(blog.created_at)}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2 hover:text-blue-600 transition-colors">
                  <a href={getBlogUrl(blog)} className="hover:underline">{blog.title}</a>
                </h3>
                <p className="text-gray-600 mb-4 line-clamp-2">{blog.excerpt}</p>
                <div className="flex justify-between items-center">
                  <a
                    href={getBlogUrl(blog)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline"
                  >
                    Read More →
                  </a>
                  <span className="flex items-center text-xs text-gray-500">
                    <FaEye className="mr-1" /> {formatViewCount(blog.viewCount)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {blogs.length > 0 && (
          <div className="text-center mt-8">
            <a
              href="/blog"
              className="inline-block px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-all duration-300 hover:shadow-md"
            >
              View All Articles
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeaturedArticles;